import { useState, useEffect } from "react";
import { COMPANY_INFO } from "@/mocks/aegis-data";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white border-b border-aegisStone py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center flex-shrink-0 cursor-pointer">
            <img
              src={COMPANY_INFO.logoUrl}
              alt="Aegis Construction Services"
              className={`h-12 w-auto object-contain transition-all duration-300 ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`font-inter text-sm tracking-wide uppercase transition-colors duration-200 cursor-pointer ${
                  scrolled
                    ? "text-aegisText hover:text-aegisGold"
                    : "text-white/90 hover:text-aegisGold"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA + Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={COMPANY_INFO.phoneHref}
              className={`font-inter text-sm font-medium transition-colors duration-200 ${
                scrolled ? "text-aegisText" : "text-white/90"
              }`}
            >
              <i className="ri-phone-line mr-1" />
              {COMPANY_INFO.phone}
            </a>
            <button
              onClick={() => handleNavClick("#contact")}
              className="bg-aegisGold text-white font-inter text-sm font-semibold px-5 py-2.5 tracking-wide uppercase whitespace-nowrap cursor-pointer hover:bg-amber-700 transition-colors duration-200"
            >
              Build Your Vision
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`lg:hidden w-8 h-8 flex flex-col justify-center gap-1.5 cursor-pointer transition-colors ${
              scrolled ? "text-aegisDark" : "text-white"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block h-0.5 w-6 bg-current transition-all duration-300 ${
                mobileOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block h-0.5 w-6 bg-current transition-all duration-300 ${
                mobileOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-0.5 w-6 bg-current transition-all duration-300 ${
                mobileOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-aegisDark transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8 px-6">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="font-cormorant text-3xl font-medium text-white hover:text-aegisGold transition-colors duration-200 cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <a
            href={COMPANY_INFO.phoneHref}
            className="mt-4 font-inter text-aegisGold text-xl"
          >
            {COMPANY_INFO.phone}
          </a>
          <button
            onClick={() => handleNavClick("#contact")}
            className="bg-aegisGold text-white font-inter text-sm font-semibold px-8 py-3 tracking-widest uppercase cursor-pointer"
          >
            Build Your Vision
          </button>
        </div>
      </div>
    </>
  );
}
